import stockApi from '@/api/stockApi'

const SCOPE_PREFIX = 'lcc.'
const TABS_KEY = 'lcc.kingdom.tabs.v1'
const PENDING_KEY = 'lcc-tabs-pending'
const API_BASE = process.env.VUE_APP_API_BASE || '/api'

let baseUpdatedAt = null
let pushTimer = null
let pushing = false
let queued = false
let pushWaiters = []
let conflictAttempt = 0
let installed = false
let onReplace = null
let memoryPendingTabs = null

function nativeGet(storage, key) {
  return Storage.prototype.getItem.call(storage, key)
}

function nativeSet(key, value) {
  Storage.prototype.setItem.call(localStorage, key, value)
}

function nativeRemove(key) {
  Storage.prototype.removeItem.call(localStorage, key)
}

function markTabsPending(raw) {
  memoryPendingTabs = raw
  try { sessionStorage.setItem(PENDING_KEY, raw) } catch (err) { /* ignore */ }
}

function readPendingTabs() {
  if (typeof memoryPendingTabs === 'string') return memoryPendingTabs
  try { return sessionStorage.getItem(PENDING_KEY) } catch (err) { return null }
}

function clearPendingTabs() {
  memoryPendingTabs = null
  try { sessionStorage.removeItem(PENDING_KEY) } catch (err) { /* ignore */ }
}

export function hasPendingLccTabs() {
  return typeof readPendingTabs() === 'string'
}

function collectEntries() {
  const entries = {}
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i)
      if (!key || key.indexOf(SCOPE_PREFIX) !== 0) continue
      const value = nativeGet(localStorage, key)
      if (typeof value === 'string') entries[key] = value
    }
  } catch (err) {
    /* ignore */
  }
  const pending = readPendingTabs()
  if (typeof pending === 'string') entries[TABS_KEY] = pending
  return entries
}

function attrListEmpty(text) {
  try {
    const list = JSON.parse(text)
    return Array.isArray(list) && !list.some((item) => String(item || '').trim())
  } catch (err) {
    return false
  }
}

function applyEntries(entries) {
  if (!entries || typeof entries !== 'object') return
  Object.keys(entries).forEach((key) => {
    if (key.indexOf(SCOPE_PREFIX) !== 0) return
    const value = entries[key]
    if (typeof value !== 'string') return
    if (key.indexOf('mainAttr') >= 0) {
      let current = ''
      try { current = nativeGet(localStorage, key) || '' } catch (err) { current = '' }
      if (current && !attrListEmpty(current) && attrListEmpty(value)) return
    }
    try { nativeSet(key, value) } catch (err) { /* ignore */ }
  })
}

export function persistKingdomTabs(list) {
  const raw = JSON.stringify(list || [])
  try { nativeSet(TABS_KEY, raw) } catch (err) { /* 本机写失败时仍靠待同步记录推到服务器 */ }
  markTabsPending(raw)
  flushLccSharedState()
}

export async function pullLccSharedState() {
  const pending = readPendingTabs()
  const res = await stockApi.getLccSharedState()
  const entries = res && res.entries
  if (entries && typeof entries === 'object' && Object.keys(entries).length) {
    applyEntries(entries)
  }
  if (typeof pending === 'string') {
    const serverTabs = entries && entries[TABS_KEY]
    if (serverTabs === pending) clearPendingTabs()
    else {
      try { nativeSet(TABS_KEY, pending) } catch (err) { /* ignore */ }
      markTabsPending(pending)
    }
  }
  baseUpdatedAt = (res && res.updatedAt) || null
  return res
}

function sendPendingKeepalive() {
  const pending = readPendingTabs()
  if (typeof pending !== 'string') return
  const entries = collectEntries()
  if (!entries[TABS_KEY]) return
  const token = (() => {
    try { return nativeGet(localStorage, 'token') || '' } catch (err) { return '' }
  })()
  const body = JSON.stringify({
    entries,
    baseUpdatedAt: baseUpdatedAt || '',
    mode: 'merge'
  })
  if (body.length > 60000) return
  try {
    fetch(`${API_BASE}/leader-command-center/shared-state`, {
      method: 'PUT',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : ''
      },
      body
    }).catch(() => {})
  } catch (err) {
    /* 刷新时发不出去就靠 session 里的待同步记录，下次打开再推 */
  }
}

async function pushNow() {
  if (pushing) {
    queued = true
    return new Promise((resolve) => {
      pushWaiters.push(resolve)
    })
  }
  pushing = true
  queued = false
  let retry = false
  try {
    const entries = collectEntries()
    if (!Object.keys(entries).length) return { ok: false, message: '没有可保存的数据' }
    const res = await stockApi.putLccSharedState({
      entries,
      baseUpdatedAt
    })
    if (res && res.conflict) {
      const pending = readPendingTabs()
      applyEntries(res.entries)
      if (typeof pending === 'string') {
        try { nativeSet(TABS_KEY, pending) } catch (err) { /* ignore */ }
        markTabsPending(pending)
      }
      baseUpdatedAt = res.updatedAt || null
      if (typeof pending === 'string' && conflictAttempt < 1) {
        conflictAttempt += 1
        retry = true
        return
      }
      conflictAttempt = 0
      if (typeof onReplace === 'function') onReplace()
      return res
    }
    if (res && res.ok) {
      conflictAttempt = 0
      baseUpdatedAt = res.updatedAt || baseUpdatedAt
      const pending = readPendingTabs()
      if (typeof pending !== 'string' || pending === entries[TABS_KEY]) clearPendingTabs()
      return res
    }
    return res || { ok: false, message: '服务器没有收下这次保存' }
  } catch (err) {
    return { ok: false, message: (err && err.message) || '服务器暂时没写上' }
  } finally {
    pushing = false
    const waiters = pushWaiters.splice(0)
    if (retry || queued || waiters.length) {
      queued = false
      const next = pushNow()
      waiters.forEach((resolve) => resolve(next))
    }
  }
}

function schedulePush() {
  if (pushTimer) clearTimeout(pushTimer)
  pushTimer = setTimeout(() => {
    pushTimer = null
    pushNow()
  }, 400)
}

export function flushLccSharedState() {
  if (pushTimer) {
    clearTimeout(pushTimer)
    pushTimer = null
  }
  return pushNow()
}

export function installLccSharedSync(options) {
  onReplace = options && options.onReplace
  if (installed) return
  installed = true
  try {
    localStorage.setItem = function setItem(key, value) {
      nativeSet(key, value)
      if (String(key || '').indexOf(SCOPE_PREFIX) === 0) schedulePush()
    }
    localStorage.removeItem = function removeItem(key) {
      nativeRemove(key)
      if (String(key || '').indexOf(SCOPE_PREFIX) === 0) schedulePush()
    }
  } catch (err) {
    /* 部分手机浏览器不允许改 localStorage.setItem，王国保存走 persistKingdomTabs */
  }
  window.addEventListener('pagehide', sendPendingKeepalive)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') sendPendingKeepalive()
  })
}
